import { h, render, Fragment, FunctionComponent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { truncateWords } from '../../../src/ts/libs/truncateWords';
import { getApi } from '../../../src/ts/libs/getApi';
import { useDebounce } from '../hooks/useDebounce';
import { useFetch } from '../hooks/useFetch';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { Error } from './../error/Error';

interface ItemProps {
  heading: string;
  uri: string;
  search?: string;
}

interface ProductProps extends ItemProps {
  img: string;
}

interface SearchResultProps {
  ranges: ItemProps[];
  categories: ItemProps[];
  solutions: ItemProps[];
  news: ItemProps[];
  productCount: number;
  products: ProductProps[];
  search: string;
  handleCloseClick: any;
  error?: boolean;
  loading?: boolean;
}

const cleanString = (string) => {
  if (!string) return '';
  const toRemove = ['{', '}', '[', ']', '-', '/', '\\', '(', ')', '*', '+', '?', '^', '$', '|'];

  const escaped = string.split('').reduce((acc, char) => {
    if (toRemove.includes(char)) {
      return acc;
    }
    return acc + '' + char;
  }, '');
  return escaped;
};

const boldString = (str, substr) => {
  const strRegExp = new RegExp(substr, 'gi');
  return { __html: str.replace(strRegExp, `<b>${substr}</b>`) };
};

const Heading = ({ heading, search, truncate = false }) => {
  if (truncate) {
    return <span dangerouslySetInnerHTML={boldString(truncateWords(heading, 6, null), search)} />;
  }
  return <span dangerouslySetInnerHTML={boldString(heading, search)} />;
};

const BasektItem: FunctionComponent<ProductProps> = ({ img, heading, uri, search }) => (
  <a href={uri} className="basket-card quick-search__block__list__basket-item">
    <picture className="basket-card__img">
      <img src={img ? img : '/assets/img/no-photo-product.png'} alt={heading} />
    </picture>
    <div className="basket-card__content">
      <div className="basket-card__body">
        <div className="basket-card__header">
          <h3 className="basket-card__heading">
            <Heading heading={heading} search={search} />
          </h3>
        </div>
      </div>
    </div>
  </a>
);

const LinkItem: FunctionComponent<ItemProps> = ({ heading, uri, search }) => (
  <li>
    <a href={uri}>
      <Heading heading={heading} search={search} truncate />
    </a>
  </li>
);

const SearchResults: FunctionComponent<SearchResultProps> = (props) => (
  <div className={`quick-search ${props?.loading ? ` is-loading` : ` not-loading`}`}>
    {props?.loading && <div className="quick-search__loading" />}
    {props.error && <Error message={`Error searching for ${props.search}`} />}

    {!props.error &&
      !props.loading &&
      props?.products?.length <= 0 &&
      props?.ranges?.length <= 0 &&
      props?.categories?.length <= 0 &&
      props?.solutions?.length <= 0 &&
      props?.news?.length <= 0 && (
        <div className="info-block info-block--alert">
          <div className="info-block__icon">
            <svg width={22} height={22} xmlns="http://www.w3.org/2000/svg">
              <path
                d="M21.667 11c0 5.892-4.776 10.667-10.667 10.667C5.11 21.667.333 16.892.333 11 .333 5.111 5.11.333 11 .333 16.89.333 21.667 5.111 21.667 11zM11 13.15a1.979 1.979 0 100 3.958 1.979 1.979 0 000-3.957zM9.122 6.04l.319 5.85a.516.516 0 00.515.487h2.088c.274 0 .5-.214.515-.488l.32-5.85a.516.516 0 00-.516-.543H9.637a.516.516 0 00-.515.544z"
                fill="currentColor"
                fillRule="evenodd"
              />
            </svg>
          </div>
          <div className="info-block__text">
            <h3>No results found</h3>
          </div>
        </div>
      )}

    {!props.error && props?.products && props?.products?.length >= 1 && (
      <div className="quick-search__column">
        <button
          type="button"
          onClick={props.handleCloseClick}
          class="button button--transparent quick-search__close"
          aria-label="Close navigation"
        >
          <span class="button__label sr-only">Close</span>
          <svg aria-hidden="true" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 0C5.371 0 0 5.371 0 12s5.371 12 12 12 12-5.371 12-12S18.629 0 12 0zm5.884 15.15a.581.581 0 010 .823l-1.916 1.91a.581.581 0 01-.823 0L12 14.71l-3.15 3.174a.581.581 0 01-.823 0l-1.91-1.916a.581.581 0 010-.823L9.29 12 6.116 8.85a.581.581 0 010-.823l1.916-1.916a.581.581 0 01.823 0L12 9.291l3.15-3.175a.581.581 0 01.823 0l1.916 1.916a.581.581 0 010 .823L14.709 12l3.175 3.15z"
              fill="currentColor"
              fill-rule="evenodd"
            ></path>
          </svg>
        </button>
        <div className="quick-search__block">
          <h4 className="quick-search__block__heading">Products</h4>
          <div className="quick-search__block__items">
            <ul className="quick-search__block__list">
              {props.products.map((item, i) => (
                <li key={i}>
                  <BasektItem {...item} search={props.search} />
                </li>
              ))}
            </ul>
          </div>
          <div className="quick-search__block__action">
            <a href={`/search?s=${props.search}`}>View all {props.productCount} product results</a>
          </div>
        </div>
      </div>
    )}
    <div className="quick-search__column quick-search__column--alt">
      {!props.error && props?.ranges && props?.ranges.length >= 1 && (
        <div className="quick-search__block">
          <h4 className="quick-search__block__heading">Ranges</h4>

          <div className="quick-search__block__items">
            <ul className="quick-search__block__list">
              {props?.ranges.map(({ heading, uri }, i) => (
                <LinkItem heading={heading} uri={uri} key={i} search={props.search} />
              ))}
            </ul>
          </div>
          <div className="quick-search__block__action">
            <a href="/products">View all Ranges</a>
          </div>
        </div>
      )}

      {!props.error && props?.categories && props?.categories.length >= 1 && (
        <div className="quick-search__block">
          <h4 className="quick-search__block__heading">Categories</h4>

          <div className="quick-search__block__items">
            <ul className="quick-search__block__list">
              {props?.categories?.length >= 1 &&
                props?.categories.map(({ heading, uri }, i) => (
                  <LinkItem heading={heading} uri={uri} key={i} search={props.search} />
                ))}
            </ul>
          </div>
          {/* <div className="quick-search__block__action">
            <a href="#">View all related Categories</a>
          </div> */}
        </div>
      )}

      {!props.error && props?.solutions && props?.solutions.length >= 1 && (
        <div className="quick-search__block">
          <h4 className="quick-search__block__heading">Solutions</h4>

          <div className="quick-search__block__items">
            <ul className="quick-search__block__list">
              {props?.solutions.map(({ heading, uri }, i) => (
                <LinkItem heading={heading} uri={uri} key={i} search={props.search} />
              ))}
            </ul>
          </div>
          <div className="quick-search__block__action">
            <a href="/solutions">View all Solutions</a>
          </div>
        </div>
      )}

      {!props.error && props?.news && props?.news.length >= 1 && (
        <div className="quick-search__block">
          <h4 className="quick-search__block__heading">News</h4>

          <div className="quick-search__block__items">
            <ul className="quick-search__block__list">
              {props?.news.map(({ heading, uri }, i) => (
                <LinkItem heading={heading} uri={uri} key={i} search={props.search} />
              ))}
            </ul>
          </div>

          <div className="quick-search__block__action">
            <a href="/news">View all News</a>
          </div>
        </div>
      )}
    </div>
  </div>
);

const Search = () => {
  const [searchTerm, setSearchTerm] = useState(undefined);
  const [isFocused, setIsFocused] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [results, setResults] = useState<SearchResultProps>(undefined);
  const { response, error, loading, fetchData } = useFetch<SearchResultProps>(`${getApi('search')}`, {});
  const ref = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  useOnClickOutside(ref, () => setSearchOpen(false));
  const handleChange = (event) => setSearchTerm(cleanString(event.target.value).replace(/\\/g, '').replace(/\//g, ''));

  useEffect(() => {
    if (response) {
      // go get the results
      setResults(response);
      setSearchOpen(true);
    } else {
      setResults(undefined);
    }
  }, [response]);

  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.trim().length >= 2) {
      // go get the results

      (async function getSearchData() {
        await fetchData({
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            search: debouncedSearchTerm.trim(),
          }),
        });
      })();
    } else {
      setResults(undefined);
    }
  }, [debouncedSearchTerm]);

  const handleCloseClick = () => {
    setSearchOpen(false);
  };

  return (
    <Fragment>
      <div
        className={`search${isFocused || searchOpen ? ` search--focused` : ``}`}
        ref={ref}
        data-test="search-results"
      >
        <form className="search__input" action="/search" method="get">
          <input
            type="text"
            className="search__term"
            placeholder="Search for anything"
            value={searchTerm}
            onInput={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            name="s"
            autocomplete="off"
            data-test="search-input"
          />

          <button
            type="submit"
            className={`search__action ${loading ? 'is-loading' : ''}`}
            aria-label="Search"
            data-test="search-button"
          >
            {loading ? (
              <svg width={48} height={48} viewBox="0 0 45 45" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                <g fill="none" fillRule="evenodd" transform="translate(1 1)" strokeWidth={2}>
                  <circle cx={22} cy={22} r={6} strokeOpacity={0}>
                    <animate
                      attributeName="r"
                      begin="1.5s"
                      dur="3s"
                      values="6;22"
                      calcMode="linear"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-opacity"
                      begin="1.5s"
                      dur="3s"
                      values="1;0"
                      calcMode="linear"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-width"
                      begin="1.5s"
                      dur="3s"
                      values="2;0"
                      calcMode="linear"
                      repeatCount="indefinite"
                    />
                  </circle>
                  <circle cx={22} cy={22} r={6} strokeOpacity={0}>
                    <animate
                      attributeName="r"
                      begin="3s"
                      dur="3s"
                      values="6;22"
                      calcMode="linear"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-opacity"
                      begin="3s"
                      dur="3s"
                      values="1;0"
                      calcMode="linear"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-width"
                      begin="3s"
                      dur="3s"
                      values="2;0"
                      calcMode="linear"
                      repeatCount="indefinite"
                    />
                  </circle>
                  <circle cx={22} cy={22} r={8}>
                    <animate
                      attributeName="r"
                      begin="0s"
                      dur="1.5s"
                      values="6;1;2;3;4;5;6"
                      calcMode="linear"
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              </svg>
            ) : (
              <svg
                aria-hidden="true"
                viewBox="0 0 48 48"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
              >
                <title>Search Icon</title>
                <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                  <path
                    d="M35.671875,32.7495362 L30.9984375,28.076555 C30.7875,27.8656381 30.5015625,27.7484621 30.2015625,27.7484621 L29.4375,27.7484621 C30.73125,26.0939361 31.5,24.0128894 31.5,21.7490479 C31.5,16.3636364 27.1359375,12 21.75,12 C16.3640625,12 12,16.3636364 12,21.7490479 C12,27.1344595 16.3640625,31.4980959 21.75,31.4980959 C24.0140625,31.4980959 26.0953125,30.729421 27.75,29.4357973 L27.75,30.1997852 C27.75,30.4997559 27.8671875,30.7856655 28.078125,30.9965824 L32.7515625,35.6695635 C33.1921875,36.1101455 33.9046875,36.1101455 34.340625,35.6695635 L35.6671875,34.3431306 C36.1078125,33.9025486 36.1078125,33.1901182 35.671875,32.7495362 Z M21.65,27.6488282 C18.3359375,27.6488282 15.65,24.9678401 15.65,21.6494141 C15.65,18.3356752 18.33125,15.65 21.65,15.65 C24.9640625,15.65 27.65,18.3309882 27.65,21.6494141 C27.65,24.963153 24.96875,27.6488282 21.65,27.6488282 Z"
                    id="Search"
                    fill="currentColor"
                  />
                </g>
              </svg>
            )}
          </button>
        </form>

        {searchOpen && (
          <Fragment>
            <div className="search__overlay" onClick={() => setSearchOpen(false)} />
            <div className="search__results">
              <SearchResults
                {...results}
                error={error}
                search={debouncedSearchTerm}
                handleCloseClick={handleCloseClick}
                loading={loading}
              />
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

try {
  const domContainer = document.querySelector('#site-search');
  if (domContainer) {
    render(<Search />, domContainer);
  }
} catch (e) {
  console.warn(e);
}
